import {
  ChangeDetectionStrategy,
  Component,
  ContentChild,
  ElementRef,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { NzTooltipModule, NzTooltipBaseDirective } from 'ng-zorro-antd/tooltip';
import { ActivityDeviationStatus, GeoLocation } from '@wilson/interfaces';
import { BehaviorSubject, Subject, Subscription, debounceTime } from 'rxjs';
import { withLatestFrom } from 'rxjs/operators';
import { ShiftCardTooltipComponent } from '../shift-card-tooltip/shift-card-tooltip.component';

@Component({
  selector: 'wilson-shift-validation-tooltip-wrapper',
  imports: [CommonModule, NzTooltipModule, ShiftCardTooltipComponent],
  templateUrl: './shift-validation-tooltip-wrapper.component.html',
  styleUrls: ['./shift-validation-tooltip-wrapper.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ShiftValidationTooltipWrapperComponent
  implements OnInit, OnDestroy
{
  private isMouseOverSubject = new Subject();
  private interactivitySubject = new BehaviorSubject(false);
  private displayTooltipSubject = new BehaviorSubject(false);

  protected tooltipStyling = {
    'max-width': '480px',
    'min-width': '480px',
  };
  @ContentChild(ElementRef) content!: ElementRef;

  @ViewChild('tooltip', { static: true }) tooltip!: NzTooltipBaseDirective;

  @HostListener('click')
  onClick(): void {
    this.hideTooltip();
    this.isMouseOverSubject.next(false); //This prevents the tooltip from being shown after a click due to debounceTime on mouse over
  }

  @HostListener('mouseover')
  onMouseOver(): void {
    this.isMouseOverSubject.next(true);
  }

  @HostListener('mouseout')
  onMouseLeave(): void {
    this.isMouseOverSubject.next(false);
  }

  @Input() showShiftValidation = true;
  @Input() set displayTooltip(value: boolean) {
    this.displayTooltipSubject.next(value);
  }
  @Input() plannedShiftStartDateTime!: string;
  @Input() plannedShiftEndDateTime!: string;
  @Input() accurateShiftStartDateTime!: string;
  @Input() shiftStartTimeDifference!: number;
  @Input() accurateShiftEndDateTime!: string;
  @Input() shiftEndTimeDifference!: number;
  @Input() shiftStartTimeType!: ActivityDeviationStatus;
  @Input() shiftEndTimeType!: ActivityDeviationStatus;
  @Input() plannedStartLocation!:
    | Pick<GeoLocation, 'name' | 'locationCode'>
    | undefined
    | null;
  @Input() plannedEndLocation!:
    | Pick<GeoLocation, 'name' | 'locationCode'>
    | undefined
    | null;
  @Input() reportedStartLocation!:
    | Pick<GeoLocation, 'name' | 'locationCode'>
    | null
    | undefined;
  @Input() reportedEndLocation!:
    | Pick<GeoLocation, 'name' | 'locationCode'>
    | null
    | undefined;
  @Input() heightClass!: string;
  @Input() tooltipPlacement: 'top' | 'left' | 'right' | 'bottom' = 'top'; // values are from https://ng.ant.design/components/tooltip/en#api

  private subscription = new Subscription();
  onTooltipExpanded(): void {
    this.tooltip.updatePosition();
  }

  setIsInteracting(value: boolean): void {
    this.interactivitySubject.next(value);
  }

  ngOnInit(): void {
    const isMouseOverSubscription = this.isMouseOverSubject
      .pipe(debounceTime(300), withLatestFrom(this.interactivitySubject))
      .subscribe((data) => {
        const isMouseOver = data[0];
        const isInteracting = data[1];

        if (isMouseOver && this.displayTooltipSubject.value) {
          this.tooltip?.show();
          this.tooltip?.updatePosition();
        } else if (!isInteracting) {
          this.hideTooltip();
        }
      });

    this.subscription.add(isMouseOverSubscription);

    const interactivitySubscription = this.interactivitySubject
      .pipe(debounceTime(100))
      .subscribe((isInteracting) => {
        if (!isInteracting) {
          this.tooltip.hide();
          this.tooltip.updatePosition();
        }
      });
    this.subscription.add(interactivitySubscription);

    const displayTooltipSubscription = this.displayTooltipSubject.subscribe(
      (shouldDisplayTooltip) => {
        if (!shouldDisplayTooltip) {
          this.tooltip.hide();
          this.tooltip.updatePosition();
        }
      },
    );
    this.subscription.add(displayTooltipSubscription);
  }

  private hideTooltip(): void {
    this.tooltip.hide();
    this.tooltip.updatePosition();
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }
}
